import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ErrorBanner } from '../../components/ErrorBanner';
import {
  accountGroupsApi,
  branchesApi,
  financialYearsApi,
  goodsPostingAccountMappingApi,
  invoiceSeriesApi,
  itemsApi,
  ledgersApi,
  partyAddressesApi,
  printTemplatesApi,
  reportsApi,
  voucherClassesApi,
  vouchersApi,
} from '../../lib/api';
import { compareMoney } from '../../lib/money';
import { usePermission } from '../../lib/permissions';
import type {
  AccountGroup,
  Branch,
  CompanyFeatures,
  FinancialYear,
  Ledger,
  TrialBalanceReport,
  Voucher,
} from '../../lib/types';
import { useCompanyStore } from '../../stores/company';
import { useSessionStore } from '../../stores/session';
import { useUiStore } from '../../stores/ui';

interface SetupSnapshot {
  loading: boolean;
  error: unknown;
  features: CompanyFeatures | null;
  branches: Branch[];
  financialYears: FinancialYear[];
  ledgers: Ledger[];
  groups: AccountGroup[];
  trialBalance: TrialBalanceReport | null;
  vouchers: Voucher[];
  refresh: () => void;
}

export interface OnboardingStatus extends SetupSnapshot {
  companyReady: boolean;
  profileReady: boolean;
  branchReady: boolean;
  financialYearReady: boolean;
  openingTied: boolean | null;
  openingZero: boolean | null;
  voucherPosted: boolean;
  booksTied: boolean | null;
  bankReady: boolean;
  capitalReady: boolean;
  progress: number;
  complete: boolean;
}

const EMPTY_SNAPSHOT: Omit<SetupSnapshot, 'refresh'> = {
  loading: false,
  error: null,
  features: null,
  branches: [],
  financialYears: [],
  ledgers: [],
  groups: [],
  trialBalance: null,
  vouchers: [],
};

function safe<T>(promise: Promise<T>, fallback: T, errors: unknown[]): Promise<T> {
  return promise.catch((error) => {
    errors.push(error);
    return fallback;
  });
}

/**
 * Setup status is deliberately derived from the live books. A completed wizard
 * is not proof that a company is ready: another operator may still be entering
 * openings, or a company may have been switched while this screen was closed.
 */
export function useOnboardingStatus(): OnboardingStatus {
  const activeCompanyId = useCompanyStore((state) => state.activeCompanyId);
  const company = useCompanyStore((state) => state.companies.find((item) => item.id === activeCompanyId) ?? null);
  const features = useCompanyStore((state) => state.features);
  const permissions = useSessionStore((state) => state.permissions);
  const booksRevision = useUiStore((state) => state.booksRevision);
  const can = usePermission();
  const [snapshot, setSnapshot] = useState<Omit<SetupSnapshot, 'refresh'>>({ ...EMPTY_SNAPSHOT });
  const [refreshKey, setRefreshKey] = useState(0);

  const canTrialBalance = can('report:trial_balance:view');
  const canVoucherView = can('voucher:journal:view') || can('voucher:contra:view');

  useEffect(() => {
    let cancelled = false;
    if (!activeCompanyId) {
      setSnapshot({ ...EMPTY_SNAPSHOT });
      return () => {
        cancelled = true;
      };
    }

    setSnapshot((current) => ({ ...current, loading: true, error: null }));
    const errors: unknown[] = [];
    Promise.all([
      safe(branchesApi.list(), { items: [], nextCursor: null }, errors),
      safe(financialYearsApi.list(), { items: [], nextCursor: null }, errors),
      safe(accountGroupsApi.flat(), { items: [], nextCursor: null }, errors),
      safe(ledgersApi.list({ limit: 200 }), { items: [], nextCursor: null }, errors),
      canTrialBalance ? safe(reportsApi.trialBalance(), null, errors) : Promise.resolve(null),
      canVoucherView
        ? safe(vouchersApi.list({ cancelled: false, limit: 200 }), { items: [], nextCursor: null }, errors)
        : Promise.resolve({ items: [], nextCursor: null }),
    ]).then(([branchResponse, yearResponse, groupResponse, ledgerResponse, trialBalance, voucherResponse]) => {
      if (cancelled) return;
      setSnapshot({
        loading: false,
        error: errors[0] ?? null,
        features,
        branches: branchResponse.items,
        financialYears: yearResponse.items,
        groups: groupResponse.items,
        ledgers: ledgerResponse.items,
        trialBalance,
        vouchers: voucherResponse.items,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [activeCompanyId, booksRevision, canTrialBalance, canVoucherView, features, permissions, refreshKey]);

  const status = useMemo(() => {
    const companyReady = Boolean(company);
    const profileReady = Boolean(features?.businessProfile);
    const branchReady = snapshot.branches.some((branch) => branch.isActive);
    const financialYearReady = snapshot.financialYears.some((year) => !year.isClosed);
    const openingTied = snapshot.trialBalance
      ? compareMoney(snapshot.trialBalance.totals.openingDebit, snapshot.trialBalance.totals.openingCredit) === 0
      : null;
    const openingZero = snapshot.trialBalance
      ? compareMoney(snapshot.trialBalance.totals.openingDebit, '0.0000') === 0 &&
        compareMoney(snapshot.trialBalance.totals.openingCredit, '0.0000') === 0
      : null;
    const voucherPosted = snapshot.vouchers.some((voucher) => !voucher.cancelledAt && voucher.state === 'regular');
    const booksTied = snapshot.trialBalance
      ? voucherPosted && compareMoney(snapshot.trialBalance.totals.debit, snapshot.trialBalance.totals.credit) === 0
      : null;
    const groupById = new Map(snapshot.groups.map((group) => [group.id, group]));
    // Contra is stricter than a cash transfer label: the server requires at
    // least one actual bank ledger, so cash-in-hand alone must not enable it.
    const bankReady = snapshot.ledgers.some((ledger) => ledger.isActive && ledger.ledgerType === 'bank');
    const capitalReady = snapshot.ledgers.some((ledger) => {
      if (!ledger.isActive) return false;
      const group = groupById.get(ledger.groupId);
      return group?.name === 'Capital Account' && ledger.name !== 'Profit & Loss A/c';
    });
    const steps = [
      companyReady,
      profileReady,
      branchReady,
      financialYearReady,
      openingTied === true,
      voucherPosted,
      booksTied === true,
    ];
    const progress = steps.filter(Boolean).length;
    return {
      companyReady,
      profileReady,
      branchReady,
      financialYearReady,
      openingTied,
      openingZero,
      voucherPosted,
      booksTied,
      bankReady,
      capitalReady,
      progress,
      complete: voucherPosted && booksTied === true,
    };
  }, [company, features, snapshot]);

  const refresh = useCallback(() => setRefreshKey((current) => current + 1), []);
  return { ...snapshot, features, refresh, ...status };
}

function StatusMark({ done, pending = false }: { done: boolean | null; pending?: boolean }) {
  if (pending) return <span className="mono text-[10px] text-[var(--text-faint)]">checking</span>;
  if (done) return <span className="mono text-[10px] font-semibold text-[var(--success)]">ready</span>;
  return <span className="mono text-[10px] font-semibold text-[var(--warn)]">next</span>;
}

function ChecklistRow({
  number,
  title,
  detail,
  done,
  pending,
  action,
}: {
  number: string;
  title: string;
  detail: string;
  done: boolean | null;
  pending?: boolean;
  action?: React.ReactNode;
}) {
  return (
    <div
      className={clsx(
        'flex items-start gap-3 border-b border-[var(--border)] px-3 py-3 last:border-b-0',
        done && 'bg-[var(--success-bg)]/30'
      )}
    >
      <span
        className={clsx(
          'mono mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px]',
          done
            ? 'border-[var(--success)] text-[var(--success)]'
            : 'border-[var(--border-strong)] text-[var(--text-faint)]'
        )}
      >
        {done ? '✓' : number}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12.5px] font-medium">{title}</span>
          <StatusMark done={done} pending={pending} />
        </div>
        <p className="mt-0.5 text-[11.5px] text-[var(--text-dim)]">{detail}</p>
      </div>
      {action}
    </div>
  );
}

export function OnboardingProgress() {
  const activeCompanyId = useCompanyStore((state) => state.activeCompanyId);
  const status = useOnboardingStatus();
  if (!activeCompanyId) return null;
  return (
    <NavLink
      to="/onboarding"
      className={({ isActive }) =>
        clsx(
          'flex items-center justify-between rounded-[3px] px-2 py-1 text-[12.5px]',
          isActive
            ? 'bg-[var(--bg-selected)] font-medium'
            : 'text-[var(--text-dim)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]'
        )
      }
    >
      <span>Getting started</span>
      <span className={clsx('mono text-[10px]', status.complete ? 'text-[var(--text-faint)]' : 'text-[var(--accent)]')}>
        {status.loading ? '…' : status.complete ? 'ready' : `${status.progress}/7`}
      </span>
    </NavLink>
  );
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const can = usePermission();
  const pushToast = useUiStore((state) => state.pushToast);
  const activeCompany = useCompanyStore((state) => state.activeCompany());
  const status = useOnboardingStatus();
  const canCreateLedger = can('master:ledger:create');
  const canViewMasters = can('master:account_group:view') && can('master:ledger:view');
  const [goodsSetup, setGoodsSetup] = useState({
    checking: true,
    mapping: false,
    items: 0,
    addresses: 0,
    standardClasses: false,
    series: 0,
    templates: 0,
  });
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      goodsPostingAccountMappingApi.get(),
      itemsApi.list(),
      partyAddressesApi.list(),
      voucherClassesApi.list(),
      invoiceSeriesApi.list(),
      printTemplatesApi.list(),
    ]).then((result) => {
      if (cancelled) return;
      const value = <T,>(index: number, fallback: T): T =>
        result[index]?.status === 'fulfilled' ? (result[index].value as T) : fallback;
      const itemResponse = value(1, { items: [] as any[] });
      const addressResponse = value(2, { items: [] as any[] });
      const classes = value(3, { items: [] as any[] });
      const series = value(4, { items: [] as any[] });
      const templates = value(5, { items: [] as any[] });
      setGoodsSetup({
        checking: false,
        mapping: result[0]?.status === 'fulfilled',
        items: itemResponse.items.length,
        addresses: addressResponse.items.length,
        standardClasses: classes.items.some(
          (entry: any) => entry.isActive && (entry.code === 'standard_sales' || entry.code === 'standard_purchase')
        ),
        series: series.items.filter((entry: any) => entry.isActive).length,
        templates: templates.items.filter((entry: any) => entry.isActive).length,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [activeCompany?.id, status.refresh]);

  function openingAction() {
    if (!canViewMasters) {
      pushToast({
        kind: 'error',
        message:
          'Opening-balance review needs Chart of Accounts view permission. Ask an administrator to grant master:account_group:view and master:ledger:view.',
      });
      return;
    }
    navigate('/coa?reportSource=opening_configuration');
  }

  function ledgerAction() {
    if (!canViewMasters) {
      pushToast({
        kind: 'error',
        message:
          'Ledger setup needs Chart of Accounts view permission. Ask an administrator to grant master:account_group:view and master:ledger:view.',
      });
      return;
    }
    navigate('/coa');
  }

  const setupLabel = status.complete ? 'Books are live' : `Setup ${status.progress} of 7 checkpoints`;
  const profileLabel = status.features?.businessProfile
    ? status.features.businessProfile.replace('_', ' ')
    : 'Choose a business profile';
  return (
    <div className="min-h-full overflow-y-auto bg-[var(--bg)]">
      <div className="mx-auto max-w-5xl px-4 py-5 lg:px-7">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[var(--border-strong)] pb-4">
          <div>
            <p className="mono text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]">
              Greenfield books / getting started
            </p>
            <h1 className="mt-1 text-[21px] font-semibold tracking-tight">
              Make {activeCompany?.name ?? 'this company'} trustworthy from day one.
            </h1>
            <p className="mt-1 max-w-2xl text-[12.5px] text-[var(--text-dim)]">
              A calm path from company setup to a balanced Trial Balance. Nothing is fabricated for you; each checkpoint
              reflects the books that are actually present.
            </p>
          </div>
          <div className="border border-[var(--border-strong)] bg-[var(--bg-elevated)] px-3 py-2 text-right">
            <p className="mono text-[10px] uppercase tracking-wide text-[var(--text-faint)]">Status</p>
            <p
              className={clsx(
                'mt-0.5 text-[13px] font-semibold',
                status.complete ? 'text-[var(--success)]' : 'text-[var(--accent)]'
              )}
            >
              {setupLabel}
            </p>
          </div>
        </div>

        {status.error ? (
          <div className="mt-3">
            <ErrorBanner error={status.error} onDismiss={() => status.refresh()} />
          </div>
        ) : null}
        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)]">
          <section className="border border-[var(--border-strong)] bg-[var(--bg-elevated)]">
            <div className="flex items-center justify-between border-b border-[var(--border)] bg-[var(--bg-inset)] px-3 py-2">
              <div>
                <p className="mono text-[10px] uppercase tracking-wide text-[var(--text-dim)]">The ledger sequence</p>
                <h2 className="text-[13px] font-semibold">Seven checks, one clean handoff</h2>
              </div>
              <span className="mono text-[11px] text-[var(--accent)]">{status.progress}/7</span>
            </div>
            <ChecklistRow
              number="1"
              title="Company identity"
              detail={
                status.companyReady
                  ? 'Company record exists and is active.'
                  : 'Create the company before entering books.'
              }
              done={status.companyReady}
              pending={status.loading}
            />
            <ChecklistRow
              number="2"
              title="Profile selected"
              detail={
                status.profileReady
                  ? `Business profile: ${profileLabel}. Features can be refined later.`
                  : 'Choose Trader, Services, Manufacturer, or another profile.'
              }
              done={status.profileReady}
              pending={status.loading}
            />
            <ChecklistRow
              number="3"
              title="Primary branch ready"
              detail={
                status.branchReady
                  ? 'At least one active branch is available for posting.'
                  : 'Create an active primary branch.'
              }
              done={status.branchReady}
              pending={status.loading}
            />
            <ChecklistRow
              number="4"
              title="Financial year open"
              detail={
                status.financialYearReady
                  ? 'An open financial year is ready for entries.'
                  : 'Create or reopen a financial year before posting.'
              }
              done={status.financialYearReady}
              pending={status.loading}
            />
            <ChecklistRow
              number="5"
              title="Opening balances reviewed"
              detail={
                status.openingTied === null
                  ? 'Trial Balance access is needed to inspect opening balances.'
                  : status.openingZero
                    ? 'Opening Dr = Cr = 0.0000. You can skip opening entry.'
                    : status.openingTied
                      ? 'Opening Dr equals Cr. The configured starting point ties.'
                      : 'Opening Dr and Cr do not tie yet. Review ledger opening values.'
              }
              done={status.openingTied}
              pending={status.loading}
              action={
                <button className="button-muted shrink-0" onClick={openingAction}>
                  Review openings
                </button>
              }
            />
            <ChecklistRow
              number="6"
              title="First valid voucher posted"
              detail={
                status.voucherPosted
                  ? 'An uncancelled regular Journal or Contra posting is present in the books.'
                  : 'Post one balanced regular Journal or Contra after your ledgers are ready.'
              }
              done={status.voucherPosted}
              pending={status.loading}
              action={
                !status.voucherPosted && (can('voucher:journal:create') || can('voucher:contra:create')) ? (
                  <button
                    className="button-primary shrink-0"
                    onClick={() =>
                      navigate(`/vouchers/new?type=${can('voucher:journal:create') ? 'journal' : 'contra'}`)
                    }
                  >
                    Post first voucher
                  </button>
                ) : undefined
              }
            />
            <ChecklistRow
              number="7"
              title="Books verified"
              detail={
                status.booksTied === null
                  ? 'Trial Balance access is needed to verify the books.'
                  : !status.voucherPosted
                    ? 'Post the first valid voucher before using Trial Balance to verify the books.'
                    : status.booksTied
                      ? 'Trial Balance Dr equals Cr. Every figure can be drilled to its source.'
                      : 'Trial Balance still has a difference; inspect openings and source vouchers.'
              }
              done={status.booksTied}
              pending={status.loading}
              action={
                <button className="button-muted shrink-0" onClick={() => navigate('/reports/trial-balance')}>
                  Open Trial Balance
                </button>
              }
            />
          </section>

          <aside className="flex flex-col gap-3">
            <section className="border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-3">
              <p className="mono text-[10px] uppercase tracking-wide text-[var(--text-dim)]">Your first posting</p>
              <h2 className="mt-1 text-[14px] font-semibold">Use the right document</h2>
              <p className="mt-1 text-[11.5px] text-[var(--text-dim)]">
                Cash and an entity-appropriate capital ledger are already ready. Use Journal for opening capital or
                adjustments; Contra only moves cash ↔ bank and needs one bank ledger.
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  className="button-muted justify-center"
                  disabled={!can('voucher:journal:create')}
                  onClick={() => navigate('/vouchers/new?type=journal')}
                >
                  <span>Journal</span>
                  <span className="kbd">F7</span>
                </button>
                <button
                  className="button-muted justify-center"
                  disabled={!can('voucher:contra:create') || !status.bankReady}
                  onClick={() => navigate('/vouchers/new?type=contra')}
                >
                  <span>Contra</span>
                  <span className="kbd">F4</span>
                </button>
              </div>
              {!status.bankReady && (
                <p className="mt-2 border border-[var(--warn)] bg-[var(--warn-bg)] px-2 py-1.5 text-[11px] text-[var(--warn)]">
                  Contra needs one bank ledger. Create it in Chart of Accounts; Cash is already ready.
                </p>
              )}
              {!status.capitalReady && (
                <p className="mt-2 border border-[var(--warn)] bg-[var(--warn-bg)] px-2 py-1.5 text-[11px] text-[var(--warn)]">
                  No capital ledger is visible. Review Chart of Accounts before posting opening capital.
                </p>
              )}
              <button
                className="mt-3 text-left text-[11.5px] text-[var(--accent)] underline-offset-2 hover:underline"
                onClick={ledgerAction}
              >
                {canCreateLedger ? 'Review chart of accounts →' : 'Ask for Chart of Accounts view permission →'}
              </button>
            </section>
            <section className="border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-3">
              <p className="mono text-[10px] uppercase tracking-wide text-[var(--text-dim)]">First goods document</p>
              <h2 className="mt-1 text-[14px] font-semibold">Set up once, then invoice</h2>
              <p className="mt-1 text-[11.5px] text-[var(--text-dim)]">
                Keep commercial masters ahead of the first Purchase or Sales voucher. The voucher computes GST and
                totals on the server; it does not guess a tax rate in the browser.
              </p>
              <p className="mt-2 border border-[var(--border)] bg-[var(--bg-inset)] px-2 py-1.5 text-[10.5px] text-[var(--text-dim)]">
                {goodsSetup.checking
                  ? 'Checking live goods setup…'
                  : `Live setup: ${goodsSetup.mapping ? 'posting map ready' : 'posting map missing'} · ${goodsSetup.standardClasses ? 'standard class ready' : 'standard class missing'} · ${goodsSetup.series} active series · ${goodsSetup.templates} print templates · ${goodsSetup.items} items · ${goodsSetup.addresses} party addresses.`}
              </p>
              <div className="mt-3 grid gap-1.5">
                <button
                  className="button-muted justify-between"
                  disabled={!can('master:goods_posting_account_mapping:view')}
                  onClick={() => navigate('/settings/posting-accounts')}
                >
                  <span>0. Posting accounts & voucher defaults</span>
                  <span>→</span>
                </button>
                <button
                  className="button-muted justify-between"
                  disabled={!can('master:item:view')}
                  onClick={() => navigate('/masters/items')}
                >
                  <span>1. Item, UOM & effective GST rate</span>
                  <span>→</span>
                </button>
                <button
                  className="button-muted justify-between"
                  disabled={!can('master:party_address:view')}
                  onClick={() => navigate('/masters/party-addresses')}
                >
                  <span>2. Party bill-to / ship-to</span>
                  <span>→</span>
                </button>
                <button
                  className="button-muted justify-between"
                  disabled={!can('master:invoice_series:view')}
                  onClick={() => navigate('/settings/invoice-series')}
                >
                  <span>3. Configure invoice series (server allocates)</span>
                  <span>→</span>
                </button>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    className="button-primary justify-center"
                    disabled={!can('voucher:purchase:create')}
                    onClick={() => navigate('/goods/purchase/new')}
                  >
                    <span>Purchase</span>
                    <span className="kbd">F9</span>
                  </button>
                  <button
                    className="button-primary justify-center"
                    disabled={!can('voucher:sales:create')}
                    onClick={() => navigate('/goods/sales/new')}
                  >
                    <span>Sales</span>
                    <span className="kbd">F8</span>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    className="button-muted justify-center"
                    disabled={!can('report:purchase_register:view')}
                    onClick={() => navigate('/goods/purchase/register')}
                  >
                    Verify Purchase register
                  </button>
                  <button
                    className="button-muted justify-center"
                    disabled={!can('report:sales_register:view')}
                    onClick={() => navigate('/goods/sales/register')}
                  >
                    Verify Sales register
                  </button>
                </div>
              </div>
            </section>
            <section className="border border-[var(--border)] bg-[var(--bg-inset)] p-3">
              <p className="mono text-[10px] uppercase tracking-wide text-[var(--text-dim)]">What comes later</p>
              <p className="mt-1 text-[11.5px] text-[var(--text-dim)]">
                Tally migration is intentionally not part of greenfield setup. Import, inventory, and GST workflows
                arrive with their later voucher domains.
              </p>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
